import { SidePanelLayerOrGroup, SidePanelLayerType } from "@nova/constants"
import { compareFractionalIndices } from "@nova/serializer/src/client/util/fractional_indexing"
import {
    LayersSortOptions,
    AssetsSortOptions,
    PDFsSortOptions,
    selectSortLayersValue,
    selectSortAssetsValue,
    selectSortPDFsValue,
} from "./slice.new"
import { Selector } from "react-redux"
import { RootState } from "integrations/redux"
import { selectAllEntitiesMap, selectPdfEntitiesMap } from "./slice"
import { useLayerSidePanelSortedEntities } from "./useSidePanelLayers"
import { usePdfSidePanelSortedEntities } from "./useSidePanelPdfs"
import { ContextMenuItem } from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import {
    useAssetsMultipleSelectedContextMenuActions,
    useLayersMultipleSelectedContextMenuActions,
    usePdfMultipleSelectedContextMenuActions,
} from "./generics/genericMultipleSelectedContextMenuActions"
import { NovaPluginIds } from "panels/controls/PluginAccessPanel/constants"

export const sidePanelModes = ["Layers", "Assets", "PDFs"] as const
export type SidePanelMode = (typeof sidePanelModes)[number]

export const MAX_LAYER_GROUP_DEPTH = 3
export const MAX_PDF_GROUP_DEPTH = 1
export const maxDepthByMode: Record<SidePanelMode, number> = {
    Layers: MAX_LAYER_GROUP_DEPTH,
    Assets: 0,
    PDFs: MAX_PDF_GROUP_DEPTH,
}

export const compareFunctionsBySortMode: Record<
    LayersSortOptions | AssetsSortOptions | PDFsSortOptions,
    (a: SidePanelLayerOrGroup, b: SidePanelLayerOrGroup, sizeMap?: Record<string, number>) => number
> = {
    None: (a, b) => compareFractionalIndices(b.index, a.index),
    Name: (a, b) => a.name.localeCompare(b.name),
    Kind: (a, b) => (a.type || "").localeCompare(b.type || ""),
    "Date Created": (a, b) => a.createdAt - b.createdAt,
    Size: (a, b, sizeMap) => (sizeMap?.[a.id] || 0) - (sizeMap?.[b.id] || 0),
}

type DataSelectors<
    TSortedEntities extends SidePanelLayerOrGroup[] = SidePanelLayerOrGroup[],
    TEntityMap extends Record<string, SidePanelLayerOrGroup> = Record<
        string,
        SidePanelLayerOrGroup
    >,
> = {
    useSortedEntities: () => TSortedEntities
    selectEntityMap: Selector<RootState, TEntityMap>
    selectSortMode: Selector<RootState, LayersSortOptions | AssetsSortOptions | PDFsSortOptions>
}

export const dataSelectorsByMode: Record<SidePanelMode, DataSelectors> = {
    Layers: {
        useSortedEntities: useLayerSidePanelSortedEntities,
        selectEntityMap: selectAllEntitiesMap,
        selectSortMode: selectSortLayersValue,
    },
    Assets: {
        useSortedEntities: () => [],
        selectEntityMap: selectAllEntitiesMap,
        selectSortMode: selectSortAssetsValue,
    },
    PDFs: {
        useSortedEntities: usePdfSidePanelSortedEntities,
        selectEntityMap: selectPdfEntitiesMap,
        selectSortMode: selectSortPDFsValue,
    },
}

export const useMultipleSelectedContextMenuActionsByMode: Record<
    SidePanelMode,
    () => ContextMenuItem[][]
> = {
    Layers: useLayersMultipleSelectedContextMenuActions,
    Assets: useAssetsMultipleSelectedContextMenuActions,
    PDFs: usePdfMultipleSelectedContextMenuActions,
}

export function getSidePanelFeatureFromLayerType(
    layerType?: SidePanelLayerType
): NovaPluginIds | undefined {
    switch (layerType) {
        case SidePanelLayerType.OrthomosaicFlight:
            return NovaPluginIds.Ortho
        case SidePanelLayerType.HotspotFlight:
            return NovaPluginIds.IR
        case SidePanelLayerType.Panorama:
            return NovaPluginIds.Panorama
        case SidePanelLayerType.ImageColorFinder:
        case SidePanelLayerType.VideoColorFinder:
            return NovaPluginIds.Colorfinder
        case SidePanelLayerType.FirePerimeter:
            return NovaPluginIds.Perimeter
        case SidePanelLayerType.VideoFlight:
            return NovaPluginIds.Video
        case SidePanelLayerType.LiveVideoFlight:
            return NovaPluginIds.LiveVideo
        case SidePanelLayerType.SatelliteFlight:
            return NovaPluginIds.Satellite
    }
}
